/**
 * QuickReplyTable - Tabela de respostas rápidas usando DataTable genérico
 * Princípios: DRY (reutiliza DataTable), SRP (apenas configuração de colunas)
 */
import React, { useMemo } from 'react';
import { QuickReply } from '../../../../types';
import DataTable, { Column } from '../../../../components/shared/DataTable';
import ActionMenu, { createEditAction, createDeleteAction, createDuplicateAction } from '../../../../components/shared/ActionMenu';

interface QuickReplyTableProps {
    replies: QuickReply[];
    isLoading?: boolean;
    onEdit: (reply: QuickReply) => void;
    onDelete: (reply: QuickReply) => void;
    onDuplicate: (reply: QuickReply) => void;
    selectedIds?: Set<string>;
    onToggleSelection?: (id: string) => void;
    onToggleAll?: () => void;
}

const QuickReplyTable: React.FC<QuickReplyTableProps> = ({
    replies, isLoading, onEdit, onDelete, onDuplicate,
    selectedIds = new Set(), onToggleSelection, onToggleAll
}) => {
    const columns: Column<QuickReply>[] = useMemo(() => [
        {
            key: 'shortcut', header: 'Atalho',
            render: (reply) => <span className="font-medium text-default">{reply.shortcut}</span>
        },
        {
            key: 'message', header: 'Mensagem',
            render: (reply) => <span className="truncate max-w-xs text-secondary">{reply.message}</span>
        },
        {
            key: 'actions', header: 'Ações', align: 'right',
            render: (reply) => (
                <ActionMenu actions={[
                    createEditAction(() => onEdit(reply)),
                    createDuplicateAction(() => onDuplicate(reply)),
                    createDeleteAction(() => onDelete(reply))
                ]} />
            )
        }
    ], [onEdit, onDuplicate, onDelete]);

    return (
        <DataTable
            columns={columns}
            data={replies}
            getRowKey={(r) => r.id}
            emptyMessage="Nenhuma resposta rápida encontrada."
            isLoading={isLoading}
            selectable
            selectedIds={selectedIds}
            onToggleSelection={onToggleSelection}
            onToggleAll={onToggleAll}
        />
    );
};

export default QuickReplyTable;
